import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import Table from '@app/src/components/Table'
import usePagination from '@app/src/hooks/pagination'
import ConfigurationCollection from '@app/src/pages/Configurations/ConfigurationCollection'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { SimpleOrganization } from '@app/src/types/organizations'
import { SolutionType } from '@app/src/types/resourceExplorer'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  ListItemText,
  Menu,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import OrganizationHeader from './OrganizationHeader'
import OrganizationRow from './OrganizationRow'

type ActualUserCount = {
  organizationId: number
  solutionType: SolutionType
  userCount: number
}

type ActualSupplierCount = {
  organizationId: number
  solutionType: SolutionType
  supplierCount: number
}

const isExceeding = (contracted: number | null | undefined, actual: number | undefined): boolean =>
  contracted !== undefined && contracted !== null && actual !== undefined && actual > contracted

const MAX_EXCEEDING_ORGS = 1000

const OrganizationCollection: React.FC = () => {
  const { formatMessage } = useIntl()
  const [organizationSearch, setOrganizationSearch] = useState('')
  const [onlyExceedingUserContract, setOnlyExceedingUserContract] = useState(false)
  const [onlyExceedingSupplierContract, setOnlyExceedingSupplierContract] = useState(false)
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null)
  const [page, pageSize, setPage, setPageSize] = usePagination()

  const activeFilterCount = [onlyExceedingUserContract, onlyExceedingSupplierContract].filter(Boolean).length

  const { data: userExceedingOrgIds, isLoading: isLoadingUserExceedingOrgIds } = useFetchResourceWithPost<
    number[],
    object
  >({
    key: [FetchKey.OrganizationsExceedingContract],
    endpoint: endpoints.organizationsExceedingContract,
    body: {},
    options: { enabled: onlyExceedingUserContract },
  })

  const { data: supplierExceedingOrgIds, isLoading: isLoadingSupplierExceedingOrgIds } = useFetchResourceWithPost<
    number[],
    object
  >({
    key: [FetchKey.OrganizationsExceedingSupplierContract],
    endpoint: endpoints.organizationsExceedingSupplierContract,
    body: {},
    options: { enabled: onlyExceedingSupplierContract },
  })

  const isFilteringExceeding = onlyExceedingUserContract || onlyExceedingSupplierContract

  // When filtering to exceeding accounts, restrict the (server-paginated) list to the union of the
  // active exceeding-org-id sets. Use a sentinel of [0] (no organization has id 0) while loading or
  // when none exceed, so the list shows nothing rather than falling back to every organization.
  const exceedingIdFilterValue = useMemo(() => {
    if (!isFilteringExceeding) {
      return undefined
    }
    const ids = new Set<number>()
    if (onlyExceedingUserContract) {
      userExceedingOrgIds?.forEach(id => ids.add(id))
    }
    if (onlyExceedingSupplierContract) {
      supplierExceedingOrgIds?.forEach(id => ids.add(id))
    }
    return ids.size ? [...ids] : [0]
  }, [
    isFilteringExceeding,
    onlyExceedingUserContract,
    onlyExceedingSupplierContract,
    userExceedingOrgIds,
    supplierExceedingOrgIds,
  ])

  const serverItemsPerPage = isFilteringExceeding
    ? Math.min(Math.max(exceedingIdFilterValue?.length ?? 0, 1), MAX_EXCEEDING_ORGS)
    : pageSize

  const { items, count, isLoading, isError } = useFetchCollectionWithPost<SimpleOrganization>({
    key: [
      FetchKey.OrganizationCollection,
      isFilteringExceeding ? 'filtered' : page,
      serverItemsPerPage,
      organizationSearch,
      (exceedingIdFilterValue ?? []).join(','),
    ],
    endpoint: endpoints.organizationsCollection,
    payload: {
      filter: [
        {
          name: 'name',
          filters: [{ value: organizationSearch, operator: Operators.Contains }],
        },
        ...(isFilteringExceeding
          ? [{ name: 'id', filters: [{ value: exceedingIdFilterValue, operator: Operators.In }] }]
          : []),
      ],
      include: ['organizationSettings'],
      pagination: {
        pageNumber: isFilteringExceeding ? 1 : page,
        itemsPerPage: serverItemsPerPage,
      },
    },
  })

  const organizationIds = useMemo(() => [...new Set(items.map(org => org.id))], [items])

  const { data: actualUserCounts } = useFetchResourceWithPost<ActualUserCount[], { organizationIds: number[] }>({
    key: [FetchKey.ActualUserCounts, ...organizationIds],
    endpoint: endpoints.actualUserCounts,
    body: { organizationIds },
    options: { enabled: organizationIds.length > 0 },
  })

  const actualUsersLookup = useMemo(() => {
    const lookup = new Map<string, number>()
    actualUserCounts?.forEach(({ organizationId, solutionType, userCount }) => {
      lookup.set(`${organizationId}-${solutionType}`, userCount)
    })
    return lookup
  }, [actualUserCounts])

  const { data: actualSupplierCounts } = useFetchResourceWithPost<ActualSupplierCount[], { organizationIds: number[] }>(
    {
      key: [FetchKey.ActualSupplierCounts, ...organizationIds],
      endpoint: endpoints.actualSupplierCounts,
      body: { organizationIds },
      options: { enabled: organizationIds.length > 0 },
    },
  )

  const actualSuppliersLookup = useMemo(() => {
    const lookup = new Map<string, number>()
    actualSupplierCounts?.forEach(({ organizationId, solutionType, supplierCount }) => {
      lookup.set(`${organizationId}-${solutionType}`, supplierCount)
    })
    return lookup
  }, [actualSupplierCounts])

  const flattenedItems = useMemo(() => {
    const rows = items.flatMap(org => {
      if (!org.organizationSettings?.length) {
        return [org]
      }

      return org.organizationSettings.map(setting => ({
        ...org,
        setting, // override the single setting to display
        actualUsers: actualUsersLookup.get(`${org.id}-${setting.solutionType}`),
        actualSuppliers: actualSuppliersLookup.get(`${org.id}-${setting.solutionType}`),
      }))
    })

    if (!isFilteringExceeding) {
      return rows
    }

    return rows.filter(row => {
      const isTransparency = row.setting?.solutionType === SolutionType.Transparency
      if (isTransparency) {
        return false
      }
      const exceedsUsers = onlyExceedingUserContract && isExceeding(row.setting?.contractedUsers, row.actualUsers)
      const exceedsSuppliers =
        onlyExceedingSupplierContract && isExceeding(row.setting?.contractedSuppliers, row.actualSuppliers)
      return exceedsUsers || exceedsSuppliers
    })
  }, [
    items,
    actualUsersLookup,
    actualSuppliersLookup,
    isFilteringExceeding,
    onlyExceedingUserContract,
    onlyExceedingSupplierContract,
  ])

  const pagedItems = useMemo(() => {
    if (!isFilteringExceeding) {
      return flattenedItems
    }
    const start = (page - 1) * pageSize
    return flattenedItems.slice(start, start + pageSize)
  }, [flattenedItems, isFilteringExceeding, page, pageSize])

  const displayCount = isFilteringExceeding ? flattenedItems.length : count

  return (
    <ConfigurationCollection
      secondaryTitle={<Typography variant="body1">{formatMessage({ id: 'organizationConfig.subtitle' })}</Typography>}
    >
      <Box flexGrow={1} display="flex" flexDirection="column" height={100}>
        <Box display="flex" alignItems="center" gap={4} mb={2}>
          <MuiTextField
            fullWidth
            variant="outlined"
            size="small"
            label={formatMessage({ id: 'organizationConfig.searchLabel' })}
            value={organizationSearch}
            onChange={e => setOrganizationSearch(e.target.value)}
          />
          <Badge badgeContent={activeFilterCount} color="primary" sx={{ mr: 6 }}>
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={e => setFilterAnchorEl(e.currentTarget)}>
              Filter
            </Button>
          </Badge>
          <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => setFilterAnchorEl(null)}>
            <MenuItem
              onClick={() => {
                setOnlyExceedingUserContract(prev => !prev)
                setPage(1)
              }}
            >
              <Checkbox edge="start" checked={onlyExceedingUserContract} disableRipple />
              <ListItemText primary="Accounts exceeding contracted users" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOnlyExceedingSupplierContract(prev => !prev)
                setPage(1)
              }}
            >
              <Checkbox edge="start" checked={onlyExceedingSupplierContract} disableRipple />
              <ListItemText primary="Accounts exceeding contracted suppliers" />
            </MenuItem>
          </Menu>
        </Box>
        <Table<SimpleOrganization>
          RowComponent={OrganizationRow}
          HeaderComponent={OrganizationHeader}
          data={pagedItems}
          isLoading={
            isLoading ||
            (onlyExceedingUserContract && isLoadingUserExceedingOrgIds) ||
            (onlyExceedingSupplierContract && isLoadingSupplierExceedingOrgIds)
          }
          count={displayCount}
          isError={isError}
          page={page}
          pageSize={pageSize}
          setPage={setPage}
          setPageSize={setPageSize}
        />
      </Box>
    </ConfigurationCollection>
  )
}

export default OrganizationCollection
